import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Auth client for Briefer.
 *
 * Tessera is the identity provider (OAuth2 / OIDC). The SPA never sees a
 * password — it kicks off the Authorization Code + PKCE flow against
 * Tessera's `/oauth2/authorize` endpoint, then exchanges the code at
 * `/oauth2/token`. Endpoint paths come from Tessera's
 * `/.well-known/openid-configuration` discovery.
 *
 * Token storage:
 *  - access_token: in-memory + localStorage (via zustand persist) for fast
 *    reload. Short-lived (~15 min).
 *  - refresh_token: Tessera returns it in the token-endpoint JSON body (NOT a
 *    cookie — confirmed with the Tessera team 2026-05-22). The SPA does not yet
 *    persist or rotate it; when the access token expires the user re-auths via
 *    the PKCE flow. Wiring silent refresh is a follow-up.
 *
 * This module is deliberately framework-light so it can be unit-tested in
 * isolation (see src/lib/auth.test.ts).
 */

export interface AuthUser {
  sub: string
  email?: string
  partnerId: string
  roles: string[]
}

interface AuthState {
  token: string | null
  user: AuthUser | null
  isAuthenticated: boolean
  setSession: (token: string, user: AuthUser) => void
  clearSession: () => void
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      setSession: (token, user) => set({ token, user, isAuthenticated: true }),
      clearSession: () => set({ token: null, user: null, isAuthenticated: false }),
    }),
    {
      name: 'briefer-auth',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
)

// ─────────────────────── PKCE helpers ──────────────────────────────────────

const VERIFIER_KEY = 'briefer.pkce.verifier'
const STATE_KEY = 'briefer.pkce.state'

const ISSUER = (): string =>
  import.meta.env.VITE_TESSERA_ISSUER_URI ?? 'http://localhost:8090'

const CLIENT_ID = (): string =>
  import.meta.env.VITE_TESSERA_CLIENT_ID ?? 'briefer-web'

const REDIRECT_URI = (): string =>
  import.meta.env.VITE_TESSERA_REDIRECT_URI ?? `${window.location.origin}/auth/callback`

/** Base64-URL encode a Uint8Array (no padding, +→-, /→_). */
export function base64UrlEncode(bytes: Uint8Array): string {
  let str = ''
  for (const b of bytes) str += String.fromCharCode(b)
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** Cryptographically random PKCE code_verifier (43 chars, RFC 7636 §4.1). */
export function generateCodeVerifier(): string {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return base64UrlEncode(bytes)
}

/** S256 challenge: BASE64URL-ENCODE(SHA256(ASCII(verifier))). */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return base64UrlEncode(new Uint8Array(digest))
}

/**
 * Kick off the PKCE flow. Generates a fresh verifier+state, stashes them in
 * sessionStorage (so a tab close drops them), and redirects to Tessera.
 */
export async function beginLogin(): Promise<void> {
  const verifier = generateCodeVerifier()
  const challenge = await generateCodeChallenge(verifier)
  const state = generateCodeVerifier() // re-use the entropy helper

  sessionStorage.setItem(VERIFIER_KEY, verifier)
  sessionStorage.setItem(STATE_KEY, state)

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: CLIENT_ID(),
    redirect_uri: REDIRECT_URI(),
    // tessera_app_roles is required so the JWT carries the
     // `tessera_app_roles.briefer` claim our backend TenantFilter
     // converts into Spring authorities.
     scope: 'openid profile email offline_access tessera_app_roles',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  })

  window.location.assign(`${ISSUER()}/oauth2/authorize?${params.toString()}`)
}

/**
 * Exchange the authorization code for an access_token. Called from the
 * /auth/callback route after Tessera redirects back. Stores the token in
 * the auth store and clears PKCE scratch state.
 */
export async function completeLogin(code: string, state: string): Promise<void> {
  const expectedState = sessionStorage.getItem(STATE_KEY)
  const verifier = sessionStorage.getItem(VERIFIER_KEY)
  if (!expectedState || expectedState !== state) {
    throw new Error('PKCE state mismatch — refusing token exchange')
  }
  if (!verifier) {
    throw new Error('PKCE verifier missing — login flow not initiated in this tab')
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: REDIRECT_URI(),
    client_id: CLIENT_ID(),
    code_verifier: verifier,
  })

  const response = await fetch(`${ISSUER()}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
    // No `credentials: 'include'` — Tessera returns the refresh_token in the
    // JSON body, not a cookie, so we don't need cross-origin credentials.
  })

  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new Error(`Token exchange failed (${response.status}): ${text}`)
  }

  const data: { access_token: string; id_token?: string } = await response.json()
  const claims = decodeJwtPayload(data.access_token)
  const user: AuthUser = {
    sub: String(claims.sub ?? ''),
    email: typeof claims.email === 'string' ? claims.email : undefined,
    partnerId: String(claims.partner_id ?? claims.pid ?? ''),
    roles: Array.isArray(claims.roles) ? (claims.roles as string[]) : [],
  }

  useAuth.getState().setSession(data.access_token, user)
  sessionStorage.removeItem(VERIFIER_KEY)
  sessionStorage.removeItem(STATE_KEY)
}

/**
 * End the local session and redirect to Tessera's end-session endpoint so
 * the IdP-side session is also dropped.
 */
export function logout(): void {
  useAuth.getState().clearSession()
  const params = new URLSearchParams({
    client_id: CLIENT_ID(),
    post_logout_redirect_uri: window.location.origin,
  })
  window.location.assign(`${ISSUER()}/oauth2/logout?${params.toString()}`)
}

/** Decode the payload of a JWT without verifying the signature. */
export function decodeJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split('.')
  if (parts.length !== 3) return {}
  const payload = parts[1]
  if (!payload) return {}
  try {
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/')
    const json = atob(padded + '==='.slice((padded.length + 3) % 4))
    return JSON.parse(json) as Record<string, unknown>
  } catch {
    return {}
  }
}
